import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {
  GetOrganizations,
  GetOrganizationsComplete,
  GetOrganizationsError,
  GetSelfOrganization,
  GetSelfOrganizationComplete,
  GetSelfOrganizationError,
  OrganizationActionTypes,
  PostOrganization,
  PostOrganizationComplete,
  PostOrganizationError
} from '../actions/organization.actions';
import {Logout} from '../actions/auth.actions';
import {catchError, exhaustMap, map} from 'rxjs/operators';
import {GetOrganizationsRequest, GetSelfOrganizationRequest, Organization, PostOrganizationRequest} from '../models/organization';
import {OrganizationService} from '../services/organization.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {Action} from '@ngrx/store';

@Injectable()
export class OrganizationEffects {

  @Effect()
  getOrganizations$ = this.actions$.pipe(
    ofType(OrganizationActionTypes.GetOrganizations),
    map((action: GetOrganizations) => action.payload),
    exhaustMap((getOrganizationsRequest: GetOrganizationsRequest) =>
      this.organizationService.getOrganizations(getOrganizationsRequest)
        .pipe(
          map((organizations: Organization[]) => {
            const organizationsWithoutTypeName = organizations.reduce(
              (_acc: Organization[], organizationWithTypeName: Organization) => {
                const {__typename: removed, ...organization} = organizationWithTypeName;

                return [
                  ..._acc,
                  organization
                ];
              }
              , []);
            return new GetOrganizationsComplete(organizationsWithoutTypeName);
          }),
          catchError(this.handleGetOrganizationError)
        )
    )
  );

  @Effect()
  postOrganization$ = this.actions$.pipe(
    ofType(OrganizationActionTypes.PostOrganization),
    map((action: PostOrganization) => action.payload),
    exhaustMap((postOrganizationRequest: PostOrganizationRequest) =>
      this.organizationService.postOrganization(postOrganizationRequest)
        .pipe(
          map((organization: Organization) => new PostOrganizationComplete(organization)),
          catchError((error: HttpErrorResponse) => of(new PostOrganizationError({
            errorMessage: this.getPostOrganizationErrorMessage(error)
          })))
        )
    )
  );

  @Effect()
  getSelfOrganization$ = this.actions$.pipe(
    ofType(OrganizationActionTypes.GetSelfOrganization),
    map((action: GetSelfOrganization) => action.payload),
    exhaustMap((getSelfOrganizationRequest: GetSelfOrganizationRequest) =>
      this.organizationService.getSelfOrganization(getSelfOrganizationRequest)
        .pipe(
          map((organization: Organization) => new GetSelfOrganizationComplete(organization)),
          catchError((error: HttpErrorResponse) => of(new GetSelfOrganizationError({
            errorMessage: error.message
          })))
        )
    )
  );

  constructor(private actions$: Actions,
              private organizationService: OrganizationService) {
  }

  private getPostOrganizationErrorMessage(error: HttpErrorResponse): string {
    const errorMsg = {
      0: 'No response from the server.\nPlease try again later.',
      422: 'Organization name already exists.'
    };

    return errorMsg[error.status] || 'Failed to create organization.\nPlease try again later.';
  }

  private handleGetOrganizationError(error: HttpErrorResponse): Observable<Action> {
    if (error.status === 401) {
      return of(new Logout());
    } else {
      return of(new GetOrganizationsError({
        errorMessage: error.message
      }));
    }
  }
}
